import type {
  LogicCondition,
  LogicConditionGroup,
  LogicRule,
  PublicSurvey,
  SurveyPage,
  SurveyQuestion,
} from '../types.js';

interface LogicContext {
  responses: Record<string, unknown>;
  variables: Record<string, unknown>;
}

interface VisibilityState {
  visibleQuestionKeys: Set<string>;
  requiredOverrides: Map<string, boolean>;
}

export function computeNextPage(
  currentPageKey: string,
  survey: PublicSurvey,
  responses: Record<string, unknown>,
  variables: Record<string, unknown> = {},
): string | null {
  const pages = survey.pages ?? [];
  const current = pages.find((page) => page.key === currentPageKey);
  if (!current) return null;

  const ctx: LogicContext = { responses, variables };
  const thankYouKeys = new Set(pages.filter((page) => page.isThankYou).map((page) => page.key));

  const globalNext = resolveSkipFromRules(survey.logicRules, pages, current, thankYouKeys, ctx);
  if (globalNext !== undefined) return globalNext;

  const pageNext = resolveSkipFromRules(current.logic?.rules, pages, current, thankYouKeys, ctx);
  if (pageNext !== undefined) return pageNext;

  const defaultNext = current.logic?.defaultNextPageKey;
  if (defaultNext) {
    if (defaultNext === '__end__' || thankYouKeys.has(defaultNext)) return null;
    if (pages.some((page) => page.key === defaultNext)) return defaultNext;
  }

  return findSequentialNextPageKey(pages, current, ctx);
}

function resolveSkipFromRules(
  rules: LogicRule[] | undefined,
  pages: SurveyPage[],
  current: SurveyPage,
  thankYouKeys: Set<string>,
  ctx: LogicContext,
): string | null | undefined {
  if (!rules?.length) return undefined;
  for (const rule of rules) {
    if (rule.enabled === false) continue;
    if (!evaluateConditionGroup(rule.conditionGroup, ctx)) continue;
    for (const action of rule.actions ?? []) {
      if (action.type === 'skipToThankYou') return null;
      if (action.type === 'skipToPage') {
        if (thankYouKeys.has(action.targetKey)) return null;
        const target = pages.find((page) => page.key === action.targetKey);
        if (target && target.order > current.order) return action.targetKey;
      }
    }
  }
  return undefined;
}

// Sequential fallback: next page by order, skipping thank-you/welcome and
// pages with defaultHidden unless one of their own rules now matches.
function findSequentialNextPageKey(
  pages: SurveyPage[],
  current: SurveyPage,
  ctx: LogicContext,
): string | null {
  const sorted = [...pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const currentIndex = sorted.findIndex((page) => page.key === current.key);
  for (let index = currentIndex + 1; index < sorted.length; index += 1) {
    const page = sorted[index];
    if (!page) continue;
    if (page.isWelcome) continue;
    if (page.isThankYou) return null;
    if (page.logic?.defaultHidden && !anyPageRuleMatches(page, ctx)) continue;
    return page.key;
  }
  return null;
}

function anyPageRuleMatches(page: SurveyPage, ctx: LogicContext): boolean {
  for (const rule of page.logic?.rules ?? []) {
    if (rule.enabled === false) continue;
    if (evaluateConditionGroup(rule.conditionGroup, ctx)) return true;
  }
  return false;
}

export function computeVisibilityState(
  page: SurveyPage,
  survey: PublicSurvey,
  responses: Record<string, unknown>,
  variables: Record<string, unknown> = {},
): VisibilityState {
  const ctx: LogicContext = { responses, variables };
  const allQuestions = survey.questions ?? [];
  const pageQuestions = allQuestions.filter((question) => question.pageKey === page.key);

  const allRules: LogicRule[] = [
    ...(survey.logicRules ?? []),
    ...(page.logic?.rules ?? []),
    ...allQuestions.flatMap((question) => question.logic?.rules ?? []),
  ];

  const showTargets = collectTargets(allRules, 'showQuestion');

  const visible = new Set<string>();
  for (const question of pageQuestions) {
    if (question.logic?.defaultHidden) continue;
    if (showTargets.has(question.key)) continue;
    visible.add(question.key);
  }

  const required = new Map<string, boolean>();
  for (const rule of allRules) {
    if (rule.enabled === false) continue;
    if (!evaluateConditionGroup(rule.conditionGroup, ctx)) continue;
    for (const action of rule.actions ?? []) {
      if (action.type === 'showQuestion') visible.add(action.targetKey);
      else if (action.type === 'hideQuestion') visible.delete(action.targetKey);
      else if (action.type === 'requireQuestion') required.set(action.targetKey, true);
      else if (action.type === 'unrequireQuestion') required.set(action.targetKey, false);
    }
  }

  return { visibleQuestionKeys: visible, requiredOverrides: required };
}

export function resolveThankYouPageKey(
  lastPage: SurveyPage | undefined,
  survey: PublicSurvey,
  responses: Record<string, unknown>,
  variables: Record<string, unknown> = {},
): string | null {
  const pages = survey.pages ?? [];
  const thankYouKeys = new Set(pages.filter((page) => page.isThankYou).map((page) => page.key));
  const ctx: LogicContext = { responses, variables };

  if (lastPage) {
    for (const rule of lastPage.logic?.rules ?? []) {
      if (rule.enabled === false) continue;
      if (!evaluateConditionGroup(rule.conditionGroup, ctx)) continue;
      for (const action of rule.actions ?? []) {
        if ((action.type === 'skipToPage' || action.type === 'skipToThankYou') && thankYouKeys.has(action.targetKey)) {
          return action.targetKey;
        }
      }
    }
    const defaultNext = lastPage.logic?.defaultNextPageKey;
    if (defaultNext && thankYouKeys.has(defaultNext)) return defaultNext;
  }

  const branches = [...(survey.thankYouBranches ?? [])].sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0),
  );
  for (const branch of branches) {
    if (evaluateConditionGroup(branch.conditionGroup, ctx)) return branch.thankYouPageKey;
  }

  const fallback = pages.find((page) => page.isThankYou);
  return fallback?.key ?? null;
}

export function orderedVisibleQuestions(
  page: SurveyPage,
  allQuestions: SurveyQuestion[],
  visibleKeys: Set<string>,
): SurveyQuestion[] {
  return allQuestions
    .filter((question) => question.pageKey === page.key)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((question) => visibleKeys.has(question.key));
}

function collectTargets(rules: LogicRule[], actionType: string): Set<string> {
  const targets = new Set<string>();
  for (const rule of rules) {
    for (const action of rule.actions ?? []) {
      if (action.type === actionType) targets.add(action.targetKey);
    }
  }
  return targets;
}

export function evaluateConditionGroup(group: LogicConditionGroup | undefined, ctx: LogicContext): boolean {
  if (!group || !group.conditions?.length) return true;
  const results = group.conditions.map((condition) => evaluateCondition(condition, ctx));
  if (group.conjunction === 'or') return results.some(Boolean);
  return results.every(Boolean);
}

export function evaluateCondition(condition: LogicCondition, ctx: LogicContext): boolean {
  const answer = condition.variableKey
    ? ctx.variables[condition.variableKey]
    : ctx.responses[condition.questionKey];
  switch (condition.operator) {
    case 'isAnswered':
      return !isEmpty(answer);
    case 'isNotAnswered':
      return isEmpty(answer);
    case 'equals':
      return valueEquals(answer, condition.value);
    case 'notEquals':
      return !valueEquals(answer, condition.value);
    case 'contains':
      return valueContains(answer, condition.value);
    case 'notContains':
      return !valueContains(answer, condition.value);
    case 'greaterThan':
      return numericCompare(answer, condition.value, (a, b) => a > b);
    case 'lessThan':
      return numericCompare(answer, condition.value, (a, b) => a < b);
    case 'greaterThanOrEqual':
      return numericCompare(answer, condition.value, (a, b) => a >= b);
    case 'lessThanOrEqual':
      return numericCompare(answer, condition.value, (a, b) => a <= b);
    case 'startsWith':
      return typeof answer === 'string' && typeof condition.value === 'string' && answer.startsWith(condition.value);
    case 'endsWith':
      return typeof answer === 'string' && typeof condition.value === 'string' && answer.endsWith(condition.value);
    default:
      return false;
  }
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function valueEquals(answer: unknown, target: unknown): boolean {
  if (Array.isArray(answer)) return answer.includes(target as string);
  if (typeof answer === 'number' && typeof target === 'string') return String(answer) === target;
  if (typeof answer === 'string' && typeof target === 'number') return answer === String(target);
  return answer === target;
}

function valueContains(answer: unknown, target: unknown): boolean {
  if (Array.isArray(answer)) return answer.includes(target as string);
  if (typeof answer === 'string' && typeof target === 'string') return answer.includes(target);
  return false;
}

function numericCompare(answer: unknown, target: unknown, op: (a: number, b: number) => boolean): boolean {
  const a = toNumber(answer);
  const b = toNumber(target);
  if (a === null || b === null) return false;
  return op(a, b);
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
